class WishlistManager {
  constructor() {
    this.storageKey = 'asmc_wishlist';
    this.wishlist = this.loadWishlist();
    this.translations = {};
    this.loadTranslations();
    this.init();
  }

  init() {
    // Initialize wishlist buttons on page load
    this.initializeButtons();
    
    // Listen for dynamic content changes
    this.observeNewProducts();
    
    // Update wishlist count in header if exists
    this.updateWishlistCount();
  }

  loadWishlist() {
    try {
      const stored = localStorage.getItem(this.storageKey);
      return stored ? JSON.parse(stored) : [];
    } catch (error) {
      console.error('Error loading wishlist:', error);
      return [];
    }
  }

  saveWishlist() {
    try {
      localStorage.setItem(this.storageKey, JSON.stringify(this.wishlist));
      this.updateWishlistCount();
    } catch (error) {
      console.error('Error saving wishlist:', error);
    }
  }

  addToWishlist(productData) {
    const existingIndex = this.wishlist.findIndex(item => item.id === productData.id);
    
    if (existingIndex === -1) {
      this.wishlist.push({
        id: productData.id,
        handle: productData.handle,
        title: productData.title,
        vendor: productData.vendor,
        price: productData.price,
        priceFormatted: productData.priceFormatted,
        image: productData.image,
        url: productData.url,
        addedAt: new Date().toISOString()
      });
      this.saveWishlist();
      return true;
    }
    return false;
  }

  removeFromWishlist(productId) {
    const index = this.wishlist.findIndex(item => item.id === productId);
    if (index > -1) {
      this.wishlist.splice(index, 1);
      this.saveWishlist();
      return true;
    }
    return false;
  }

  toggleWishlist(productData) {
    const isInWishlist = this.isInWishlist(productData.id);
    
    if (isInWishlist) {
      this.removeFromWishlist(productData.id);
    } else {
      this.addToWishlist(productData);
    }

    if (typeof window.renderWishlist === "function") {
      window.renderWishlist();
      window.QuickbuyDrawer?.setupQuickbuyButtons();
    }

    return !isInWishlist;
  }

  isInWishlist(productId) {
    return this.wishlist.some(item => item.id === productId);
  }

  getWishlist() {
    return [...this.wishlist];
  }

  clearWishlist() {
    this.wishlist = [];
    this.saveWishlist();
  }

  updateWishlistCount() {
    const countElements = document.querySelectorAll('.asmc-wishlist__count');
    countElements.forEach(element => {
      element.textContent = this.wishlist.length;
      element.style.display = this.wishlist.length > 0 ? 'block' : 'none';
    });
  }

  loadTranslations() {
    // Get translations from data attributes or script tag
    const translationScript = document.getElementById('wishlist-translations');
    if (translationScript) {
      try {
        this.translations = JSON.parse(translationScript.textContent);
      } catch (error) {
        console.error('Error loading wishlist translations:', error);
      }
    }
  }

  getTranslation(key) {
    return this.translations[key] || key;
  }

  initializeButtons() {
    const buttons = document.querySelectorAll('.asmc-wishlist__button');
    buttons.forEach(button => this.setupButton(button));
  }

  setupButton(button) {
    const productId = button.dataset.productId;
    const productHandle = button.dataset.productHandle;
    const productTitle = button.dataset.productTitle;
    const productVendor = button.dataset.productVendor;
    const productPrice = button.dataset.productPrice;
    const productPriceFormatted = button.dataset.productPriceFormatted;
    const productImage = button.dataset.productImage;
    const productUrl = button.dataset.productUrl;

    // Update button state
    if (this.isInWishlist(productId)) {
      button.classList.add('asmc-wishlist__button--active');
      button.setAttribute('aria-label', this.getTranslation('remove_from_wishlist'));
    } else {
      button.classList.remove('asmc-wishlist__button--active');
      button.setAttribute('aria-label', this.getTranslation('add_to_wishlist'));
    }

    // Remove existing listeners to prevent duplicates
    const newButton = button.cloneNode(true);
    button.parentNode.replaceChild(newButton, button);

    // Add click listener
    newButton.addEventListener('click', (event) => {
      event.preventDefault();
      event.stopPropagation();

      const productData = {
        id: productId,
        handle: productHandle,
        title: productTitle,
        vendor: productVendor,
        price: productPrice,
        priceFormatted: productPriceFormatted,
        image: productImage,
        url: productUrl
      };

      const isNowInWishlist = this.toggleWishlist(productData);
      
      if (isNowInWishlist) {
        newButton.classList.add('asmc-wishlist__button--active');
        newButton.setAttribute('aria-label', this.getTranslation('remove_from_wishlist'));
        this.showNotification(this.getTranslation('added_to_wishlist'));
      } else {
        newButton.classList.remove('asmc-wishlist__button--active');
        newButton.setAttribute('aria-label', this.getTranslation('add_to_wishlist'));
        this.showNotification(this.getTranslation('removed_from_wishlist'));
      }

      // Update all buttons for this product
      this.updateButtonsForProduct(productId);
    });
  }

  updateButtonsForProduct(productId) {
    const buttons = document.querySelectorAll(`.asmc-wishlist__button[data-product-id="${productId}"]`);
    const cartDrawerButtons = document.querySelector('#opus-shadow-container')?.shadowRoot.querySelectorAll(`.asmc-wishlist__button[data-product-id="${productId}"]`);
    const isInWishlist = this.isInWishlist(productId);
    
    [...buttons , ...cartDrawerButtons].forEach(button => {
      if (isInWishlist) {
        button.classList.add('asmc-wishlist__button--active');
        button.setAttribute('aria-label', this.getTranslation('remove_from_wishlist'));
      } else {
        button.classList.remove('asmc-wishlist__button--active');
        button.setAttribute('aria-label', this.getTranslation('add_to_wishlist'));
      }
    });
  }

  showNotification(message) {
    // Create notification element
    const notification = document.createElement('div');
    notification.className = 'asmc-wishlist__notification';
    notification.textContent = message;
    
    // Add to body
    document.body.appendChild(notification);
    
    // Trigger animation
    setTimeout(() => notification.classList.add('asmc-wishlist__notification--visible'), 10);
    
    // Remove after delay
    setTimeout(() => {
      notification.classList.remove('asmc-wishlist__notification--visible');
      setTimeout(() => notification.remove(), 300);
    }, 3000);
  }

  observeNewProducts() {
    // Create observer for dynamically loaded products
    const observer = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        mutation.addedNodes.forEach((node) => {
          if (node.nodeType === 1) { // Element node
            const buttons = node.querySelectorAll('.asmc-wishlist__button');
            buttons.forEach(button => this.setupButton(button));
          }
        });
      });
    });

    // Start observing
    observer.observe(document.body, {
      childList: true,
      subtree: true
    });
  }
}

// Initialize wishlist manager when DOM is ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => {
    window.wishlistManager = new WishlistManager();
  });
} else {
  window.wishlistManager = new WishlistManager();
} 